import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getCategoryList,
  getResourceList,
  getResourceById,
  Category,
  ResourceItem,
  ResourceDetails,
  UpdateResourcePayload,
  updateResourceById,
  CreateResourcePayload,
  createResource,
  deleteResource,
} from "../../apis/resourceApis";
import { toast } from "react-toastify";

/* ================= STATE ================= */

interface ResourceState {
  categories: Category[];
  resources: ResourceItem[];
  count: number;
  selectedResource: ResourceDetails | null;
  loading: boolean;
  error: string | null;
}

const initialState: ResourceState = {
  categories: [],
  resources: [],
  count: 0,
  selectedResource: null,
  loading: false,
  error: null,
};

/* ================= THUNKS ================= */

/* ---- Fetch Categories ---- */
export const fetchResourceCategoriesThunk = createAsyncThunk<
  Category[],
  void,
  { rejectValue: string }
>("resource/fetchCategories", async (_, { rejectWithValue }) => {
  try {
    const response = await getCategoryList();

    if (!response.status) {
      return rejectWithValue(response.message);
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch categories",
    );
  }
});

/* ---- Fetch Resource List ---- */
export const fetchResourceListThunk = createAsyncThunk<
  { resources: ResourceItem[]; count: number },
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  "resource/fetchResources",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await getResourceList(page, limit);

      if (!response.status) {
        return rejectWithValue("Failed to fetch resources");
      }

      return {
        resources: response.data.row,
        count: response.data.count,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch resources",
      );
    }
  },
);

/* ---- Fetch Resource By ID ---- */
export const fetchResourceByIdThunk = createAsyncThunk<
  ResourceDetails,
  string,
  { rejectValue: string }
>("resource/fetchResourceById", async (resourceId, { rejectWithValue }) => {
  try {
    const response = await getResourceById(resourceId);

    if (!response.status) {
      return rejectWithValue("Failed to fetch resource details");
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch resource details",
    );
  }
});

/* ---- Create Resource ---- */
export const createResourceThunk = createAsyncThunk<
  void,
  { payload: CreateResourcePayload },
  { rejectValue: string }
>("resource/createResource", async ({ payload }, { rejectWithValue }) => {
  try {
    const response = await createResource(payload);

    if (!response.status) {
      return rejectWithValue("Failed to create resource");
    }
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to create resource",
    );
  }
});

export const updateResourceThunk = createAsyncThunk<
  void,
  { resourceID: string; payload: UpdateResourcePayload },
  { rejectValue: string }
>(
  "resource/updateResource",
  async ({ resourceID, payload }, { rejectWithValue }) => {
    try {
      const response = await updateResourceById(resourceID, payload);

      if (!response.status) {
        return rejectWithValue("Failed to update resource");
      }
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to update resource",
      );
    }
  },
);

/* ===== DELETE Resource ===== */
export const deleteResourceThunk = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("resource/delete", async (resourceId, { rejectWithValue }) => {
  try {
    const response = await deleteResource(resourceId);

    if (!response.status) {
      return rejectWithValue("Failed to delete Resource");
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to delete Resource",
    );
  }
});

/* ================= SLICE ================= */

const resourceSlice = createSlice({
  name: "resource",
  initialState,
  reducers: {
    resetResourceState: () => initialState,
    clearSelectedResource: (state) => {
      state.selectedResource = null;
    },
  },
  extraReducers: (builder) => {
    const getErrorMessage = (payload: unknown, fallback: string) =>
      typeof payload === "string" ? payload : fallback;

    builder
      /* ---- Fetch Categories ---- */
      .addCase(fetchResourceCategoriesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResourceCategoriesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchResourceCategoriesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to fetch categories",
        );
        toast.error(state.error);
      })

      /* ---- Fetch Resource List ---- */
      .addCase(fetchResourceListThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResourceListThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.resources = action.payload.resources;
        state.count = action.payload.count;
      })
      .addCase(fetchResourceListThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to fetch resources",
        );
        toast.error(state.error);
      })

      /* ---- Fetch Resource By ID ---- */
      .addCase(fetchResourceByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedResource = null;
      })
      .addCase(fetchResourceByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedResource = action.payload;
      })
      .addCase(fetchResourceByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to fetch resource details",
        );
        toast.error(state.error);
      })

      /* ---- Create Resource ---- */
      .addCase(createResourceThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createResourceThunk.fulfilled, (state) => {
        state.loading = false;
        toast.success("Resource created successfully");
      })
      .addCase(createResourceThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to create resource",
        );
        toast.error(state.error);
      })

      /* ---- Update Resource ---- */
      .addCase(updateResourceThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateResourceThunk.fulfilled, (state) => {
        state.loading = false;
        toast.success("Resource updated successfully");
      })
      .addCase(updateResourceThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to update resource",
        );
        toast.error(state.error);
      })

      /* ---- Delete Resource ---- */
      .addCase(deleteResourceThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteResourceThunk.fulfilled, (state) => {
        state.loading = false;
        toast.success("Resource deleted successfully");
      })
      .addCase(deleteResourceThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to delete resource",
        );
        toast.error(state.error);
      });
  },
});

/* ================= EXPORTS ================= */

export const { resetResourceState, clearSelectedResource } =
  resourceSlice.actions;

export default resourceSlice.reducer;
